'use client';

import React, { useMemo } from 'react';

import Contentlet from '@/components/shared/contentlet';
import LinkNoPrefetch from '@/components/link-noprefetch';
import { DotCardImage } from '@/components/shared/dotCardImage';
import { formatShotDate } from '@/app/utils/date.utils';
import { encodeAssetUrl } from '@/app/utils/stringUtils';
import { getFirstCategoryName, getAllCategoryNames } from '../utils';
import { DotBlockEditor } from '@/components/shared/decodeTextBlock';
import Announcement from '../../announcement';
import Author from '@/components/shared/author';

const TagIcon = () => (
    <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        className="shrink-0">
        <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z" />
    </svg>
);

function ExtraCategoriesBadge({ extraCategories }) {
    if (!extraCategories?.length) return null;

    const tooltipId = 'extra-categories-tooltip';

    return (
        <div className="pointer-events-none relative z-20 flex items-center">
            <button
                type="button"
                aria-describedby={tooltipId}
                className="group pointer-events-auto flex cursor-default items-center gap-1 rounded-full bg-fuschia-700 px-2 py-0.5 text-white">
                <TagIcon />
                <span className="text-xs font-semibold">+{extraCategories.length}</span>
                <div
                    id={tooltipId}
                    role="tooltip"
                    className="pointer-events-none absolute bottom-full left-0 z-20 mb-2 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus:opacity-100">
                    <div className="relative whitespace-nowrap rounded-md bg-blue-700 px-3 py-1.5 text-xs font-medium text-white shadow-lg">
                        {extraCategories.join(' · ')}
                        <div className="absolute left-3 top-full border-4 border-transparent border-t-blue-700" />
                    </div>
                </div>
            </button>
        </div>
    );
}

function NormalizedCard({ data, priority = false, className = '' }) {
    const {
        date,
        image,
        title,
        description,
        url,
        category,
        extraCategories = [],
        showDate,
        thumbnailAlt,
        showCategoryBadge,
        headerType,
        showAuthor,
        author,
        horizontalCard
    } = data;
    const { month, day, year } = formatShotDate(date);

    const validHeaderType = typeof headerType === 'string' ? headerType : 'h3';

    const DescriptionContent = useMemo(
        () => () => {
            try {
                const parsedDescription =
                    description instanceof Object ? description : JSON.parse(description);

                return (
                    <DotBlockEditor
                        className="show-first-child-only text-sm *:line-clamp-5"
                        blocks={parsedDescription}
                    />
                );
            } catch (e) {
                return !!description && <p className="line-clamp-5 text-sm">{description}</p>;
            }
        },
        [description]
    );

    const img = Array.isArray(image) ? image[0] : image;
    let normalizedSrc;
    if (img && typeof img === 'object' && img.identifier) {
        normalizedSrc = encodeAssetUrl(`/dA/${img.identifier}`);
    } else if (typeof img === 'string') {
        normalizedSrc = encodeAssetUrl(img);
    } else {
        normalizedSrc = undefined;
    }

    if (horizontalCard) {
        return (
            <Contentlet contentlet={data.originalContentlet}>
                <div
                    className={`card-hover-pop relative flex size-full flex-col gap-4 rounded-xl p-3 md:flex-row md:gap-6${className ? ` ${className}` : ''}`}>
                    <LinkNoPrefetch href={url || '/'} className="absolute inset-0 z-10" />
                    <div className="relative aspect-[4/3] w-full shrink-0 overflow-hidden rounded-xl md:aspect-auto md:w-[45%]">
                        <DotCardImage
                            quality={35}
                            src={normalizedSrc}
                            alt={thumbnailAlt || title || 'Content image'}
                            priority={priority}
                            deviceType={data.deviceType}
                        />
                    </div>
                    <div className="flex flex-1 flex-col justify-center gap-3">
                        <div className="flex items-center gap-2">
                            {showDate && (
                                <div className="rounded-lg bg-fuschia px-3 font-semibold text-white">
                                    <span className="text-center text-sm">
                                        {day} {month} {year}
                                    </span>
                                </div>
                            )}
                            {!showCategoryBadge && category && (
                                <div className="flex items-center gap-1">
                                    <span className="w-min whitespace-nowrap text-sm font-semibold text-fuschia-700 hover:underline">
                                        {category}
                                    </span>
                                    <ExtraCategoriesBadge extraCategories={extraCategories} />
                                </div>
                            )}
                        </div>
                        {React.createElement(
                            validHeaderType,
                            {
                                className:
                                    'text-lg font-bold leading-tight text-blue-700 md:text-xl dark:text-white'
                            },
                            title
                        )}
                        <DescriptionContent />
                        {author && (
                            <Author
                                className="relative z-10 mt-1"
                                author={Array.isArray(author) ? author[0] : author}
                                priority={false}
                            />
                        )}
                    </div>
                </div>
            </Contentlet>
        );
    }

    return (
        <Contentlet contentlet={data.originalContentlet}>
            <div
                className={`card-hover-pop relative flex size-full flex-col gap-3 rounded-xl p-3${className ? ` ${className}` : ''}`}>
                <LinkNoPrefetch href={url || '/'} className="absolute inset-0 z-10" />
                <div className="relative aspect-video w-full overflow-hidden rounded-xl">
                    <DotCardImage
                        quality={100}
                        src={normalizedSrc}
                        alt={thumbnailAlt || title || 'Content image'}
                        priority={priority}
                        deviceType={data.deviceType}
                    />

                    {/* {showDate && (
                        <div className="z-5 absolute left-3 top-3 flex min-h-16 w-fit flex-col rounded bg-custom-overlay px-3 py-2 text-white">
                            <span className="text-center text-2xl font-normal">{day}</span>
                            <span className="text-center text-sm">
                                {month} {year}
                            </span>
                        </div>
                    )} */}
                    {showCategoryBadge && category && (
                        <div className="z-5 absolute left-[32px] top-[20px] rounded-[50px] bg-custom-cloudy px-[16px] py-[4px] text-[10px] text-white">
                            {category}
                        </div>
                    )}
                </div>
                <div className="flex h-fit flex-col gap-3">
                    <div className="flex items-center gap-2">
                        {showDate && (
                            <div className="rounded-lg bg-fuschia px-2 font-semibold text-white">
                                <span className="text-center text-sm">
                                    {day} {month} {year}
                                </span>
                            </div>
                        )}
                        {!showCategoryBadge && category && (
                            <div className="flex items-center gap-1">
                                <span className="w-min whitespace-nowrap text-sm font-semibold text-fuschia-700 hover:underline">
                                    {category}
                                </span>
                                <ExtraCategoriesBadge extraCategories={extraCategories} />
                            </div>
                        )}
                    </div>
                    {React.createElement(
                        validHeaderType,
                        { className: 'line-clamp-3 text-xl text-blue-700 dark:text-white' },
                        title
                    )}
                    <DescriptionContent />
                </div>
                {showAuthor && author && (
                    <Author
                        className="relative z-10"
                        author={Array.isArray(author) ? author[0] : author}
                        priority={false}
                    />
                )}
            </div>
        </Contentlet>
    );
}

function NormalizedAnnouncement({ data }) {
    const { date, title, description, url, type } = data;

    return (
        <Contentlet contentlet={data.originalContentlet}>
            <Announcement
                announcementDate={date}
                description={description}
                url={url}
                type1={type}
                title={title}
            />
        </Contentlet>
    );
}

export const CollectionCard = ({
    headerType,
    contentlet,
    contenttype,
    showCategory = false,
    horizontalCard,
    showAuthor,
    priority = false,
    deviceType = 'desktop',
    className = ''
}) => {
    const normalizedData = normalizeContentletData(
        headerType,
        contentlet,
        contenttype,
        showCategory,
        horizontalCard,
        showAuthor,
        deviceType
    );

    if (contenttype === 'Announcement')
        return (
            <div className={`relative ${className || ''}`}>
                <NormalizedAnnouncement data={normalizedData} />
            </div>
        );
    return <NormalizedCard data={normalizedData} priority={priority} className={className} />;
};

const normalizeContentletData = (
    headerType,
    contentlet,
    contenttype,
    showCategory,
    horizontalCard,
    showAuthor,
    deviceType
) => {
    let { postingDate, URL_MAP_FOR_CONTENT } = contentlet;
    let type,
        showCategoryBadge,
        image,
        title,
        description,
        category,
        extraCategories = [],
        date,
        showDate,
        thumbnailAlt,
        author;

    showDate = true;

    switch (contenttype) {
        case 'Video':
            image = contentlet.thumbnail;
            title = contentlet.title.replace(/\.[^/.]+$/, '');
            author = contentlet.author;
            thumbnailAlt = contentlet.thumbnailAlt;
            description = contentlet.description;
            URL_MAP_FOR_CONTENT = `/videos/${contentlet.urlTitle}`;
            date = contentlet.publishDate;
            showDate = false;
            break;
        case 'Podcast':
            image = contentlet.thumbnail;
            title = contentlet.title.replace(/\.[^/.]+$/, '');
            thumbnailAlt = contentlet.thumbnailAlt;
            description = contentlet.description;
            URL_MAP_FOR_CONTENT = `/podcast/${contentlet.urlTitle}`;
            date = contentlet.publishDate;
            showDate = false;
            break;
        case 'Blog':
            image = contentlet.identifier;
            title = contentlet.title;
            author = contentlet.author;
            thumbnailAlt = contentlet.thumbnailAlt;
            description = contentlet.teaser;
            category = showCategory ? getFirstCategoryName(contentlet.categories) : null;
            extraCategories = showCategory
                ? getAllCategoryNames(contentlet.categories).slice(1)
                : [];
            URL_MAP_FOR_CONTENT = `/blog/${contentlet.urlTitle}`;
            date = contentlet.postingDate;
            break;
        case 'CaseStudy':
            image = contentlet.image;
            title = contentlet.title;
            author = contentlet.author;
            thumbnailAlt = contentlet.thumbnailAlt;
            description = contentlet.subheading;
            category = showCategory ? getFirstCategoryName(contentlet.industry) : null;
            URL_MAP_FOR_CONTENT = `/case-studies/${contentlet.urlTitle}`;
            date = contentlet.publishDate;
            showDate = false;
            break;
        case 'ResourceLibrary':
            image = contentlet.image;
            title = contentlet.title;
            author = contentlet.author;
            description = contentlet.teaser;
            category =
                contentlet?.categories[0]?.categoryName ||
                getFirstCategoryName(contentlet?.categories);
            date = contentlet.publishDate;
            showDate = contentlet.showDate;
            showCategoryBadge = contentlet.showCategoryBadge;
            break;
        case 'News':
            image = contentlet.image;
            title = contentlet.title;
            author = contentlet.author;
            thumbnailAlt = contentlet.thumbnailAlt;
            description = contentlet.teaser || contentlet.description;
            category = showCategory ? getFirstCategoryName(contentlet.categories) : null;
            date = contentlet.publishDate;
            break;
        case 'Announcement':
            title = contentlet.title;
            description = contentlet.description;
            author = contentlet.author;
            URL_MAP_FOR_CONTENT = contentlet.url;
            type = contentlet.type1;
            date = contentlet.announcementDate;
            break;
        default:
            image = contentlet.image;
            title = contentlet.title;
            author = contentlet.author;
            thumbnailAlt = contentlet.thumbnailAlt;
            description = contentlet.description || contentlet.teaser;
            category = showCategory ? getFirstCategoryName(contentlet.categories) : null;
            date = contentlet.publishDate;
            showDate = contentlet.showDate;
    }

    return {
        postingDate,
        image,
        title,
        description,
        url: URL_MAP_FOR_CONTENT,
        category,
        extraCategories,
        originalContentlet: contentlet,
        date,
        showDate,
        showCategoryBadge,
        showAuthor,
        thumbnailAlt,
        type,
        headerType,
        horizontalCard,
        author,
        deviceType
    };
};
